import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Users, Calendar, ClipboardList, X } from "lucide-react";
import { api, fmtNGN, formatErrorDetail } from "../lib/api";
import { EmptyState, ErrorState, Spinner } from "../components/LoadState";
import { toast } from "sonner";

const PLATE_PATTERN = /^[A-Z]{1,3}-\d{1,3}-[A-Z]{1,3}$/;
const PLATE_ERROR = "Use 1-3 letters, dash, 1-3 numbers, dash, then 1-3 letters. Example: ABC-123-XYZ";

const formatPlate = (value) => {
  const groups = ["", "", ""];
  let groupIndex = 0;

  for (const char of value.toUpperCase()) {
    if (char === "-") {
      if (groups[groupIndex] && groupIndex < 2) groupIndex += 1;
    } else if (groupIndex === 0 && /[A-Z]/.test(char)) {
      if (groups[0].length < 3) groups[0] += char;
    } else if ((groupIndex === 0 || groupIndex === 1) && /\d/.test(char)) {
      if (groups[0] && groupIndex === 0) groupIndex = 1;
      if (groups[1].length < 3) groups[1] += char;
    } else if ((groupIndex === 1 || groupIndex === 2) && /[A-Z]/.test(char)) {
      if (groups[1] && groupIndex === 1) groupIndex = 2;
      if (groupIndex === 2 && groups[2].length < 3) groups[2] += char;
    }
  }

  return groups.slice(0, groupIndex + 1).join("-");
};

export function NewEntryModal({ open, onClose, onCreated }) {
  const [staff, setStaff] = useState([]);
  const [products, setProducts] = useState([]);
  const [plate, setPlate] = useState("");
  const [productId, setProductId] = useState("");
  const [pallets, setPallets] = useState(2);
  const [extraCrates, setExtraCrates] = useState(0);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingForm(true);
      setLoadError("");
      try {
        const [sr, pr] = await Promise.all([api.get("/staff"), api.get("/products")]);
        setStaff(sr.data);
        setProducts(pr.data);
        if (pr.data.length && !productId) setProductId(pr.data[0].id);
      } catch (e) {
        const msg = e.userMessage || "Failed to load form data.";
        setLoadError(msg);
        toast.error(msg);
      } finally {
        setLoadingForm(false);
      }
    })();
    // reset form on open
    setPlate(""); setPallets(2); setExtraCrates(0); setSelectedStaff([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);
  const totalCrates = useMemo(
    () => (product ? Number(pallets || 0) * product.crates_per_pallet + Number(extraCrates || 0) : 0),
    [pallets, extraCrates, product]
  );
  const totalAmount = useMemo(
    () => (product ? totalCrates * product.rate_per_crate : 0),
    [totalCrates, product]
  );
  const perStaff = selectedStaff.length ? totalAmount / selectedStaff.length : 0;
  const plateError = plate && !PLATE_PATTERN.test(plate) ? PLATE_ERROR : "";

  const toggleStaff = (id) => {
    setSelectedStaff((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));
  };

  const submit = async () => {
    if (!plate.trim()) return toast.error("Enter plate number");
    if (!PLATE_PATTERN.test(plate)) return toast.error(PLATE_ERROR);
    if (!productId) return toast.error("Pick a product");
    if (!Number(pallets)) return toast.error("Enter pallets loaded");
    if (selectedStaff.length === 0) return toast.error("Select at least one staff");
    setSaving(true);
    try {
      await api.post("/entries", {
        plate_number: plate,
        loads: [{
          product_id: productId,
          pallets_loaded: Number(pallets),
          extra_crates: Number(extraCrates || 0),
          staff_ids: selectedStaff,
        }],
      });
      onCreated?.();
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 overflow-hidden max-w-lg w-[95vw] rounded-2xl" data-testid="new-entry-dialog">
        <DialogTitle className="sr-only">New Entry</DialogTitle>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="font-display text-xl font-bold">New Entry</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Calendar size={12} /> {today}</div>
          </div>
          <button data-testid="new-entry-close" onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-secondary flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {loadError ? <ErrorState message={loadError} /> : null}
          {loadingForm ? <Spinner label="Loading form data..." /> : null}

          <div>
            <div className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Users size={14} /> Select Staff ({selectedStaff.length} selected)
            </div>
            <div className="grid grid-cols-2 gap-2">
              {!loadingForm && staff.length === 0 ? (
                <div className="col-span-2">
                  <EmptyState title="No staff available" message="Add staff before creating an entry." />
                </div>
              ) : null}
              {staff.map((s) => {
                const active = selectedStaff.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`staff-toggle-${s.id}`}
                    onClick={() => toggleStaff(s.id)}
                    className={`text-left text-sm px-3 py-2.5 rounded-lg border transition ${
                      active ? "bg-primary text-primary-foreground border-primary font-semibold" : "bg-white border-border hover:bg-secondary"
                    }`}
                  >
                    {s.name}{active ? " ✓" : ""}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold mb-1.5 block">Vehicle Plate Number</label>
            <input
              data-testid="new-entry-plate"
              value={plate}
              onChange={(e) => setPlate(formatPlate(e.target.value))}
              placeholder="ABC-123-XYZ"
              maxLength={11}
              pattern="[A-Z]{1,3}-[0-9]{1,3}-[A-Z]{1,3}"
              aria-invalid={!!plateError}
              aria-describedby={plateError ? "plate-error" : undefined}
              className={`w-full px-3 py-2.5 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary ${
                plateError ? "border-red-500" : "border-input"
              }`}
            />
            {plateError ? <p id="plate-error" className="mt-1 text-xs text-red-600">{plateError}</p> : null}
          </div>

          <div>
            <label className="text-sm font-semibold mb-1.5 block">Drink Type</label>
            <select
              data-testid="new-entry-product"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
            >
              {!products.length ? <option value="">No products available</option> : null}
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.size}) · {p.crates_per_pallet} crates/pallet · {fmtNGN(p.rate_per_crate)}/crate
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Pallets</label>
              <input
                data-testid="new-entry-pallets"
                type="number"
                min="1"
                value={pallets}
                onChange={(e) => setPallets(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Extra crates</label>
              <input
                data-testid="new-entry-extra"
                type="number"
                min="0"
                value={extraCrates}
                onChange={(e) => setExtraCrates(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              />
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 mb-2">
              <ClipboardList size={14} /> Live Calculation
            </div>
            <div className="space-y-1.5 text-sm">
              <Row label="Pallets × crates/pallet + extras" value={`${pallets || 0} × ${product?.crates_per_pallet || 0} + ${extraCrates || 0}`} />
              <Row label="Total crates" value={totalCrates} />
              <Row label={`Rate × crates`} value={product ? `${fmtNGN(product.rate_per_crate)} × ${totalCrates}` : "—"} />
              <div className="h-px bg-emerald-200 my-2" />
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Total</span>
                <span className="text-money text-xl" data-testid="calc-total">{fmtNGN(totalAmount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Each staff receives</span>
                <span className="text-emerald-600 font-semibold text-sm" data-testid="calc-each">{fmtNGN(perStaff)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t border-border bg-white">
          <button
            data-testid="new-entry-cancel"
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-border bg-white hover:bg-secondary transition font-semibold"
          >
            Cancel
          </button>
          <button
            data-testid="new-entry-save"
            onClick={submit}
            disabled={saving || loadingForm || !!loadError}
            className="flex-1 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:brightness-110 transition disabled:opacity-70"
          >
            {saving ? "Saving..." : "Save Entry"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}
